
import { useState, useEffect } from 'react';
import { ChatInput } from '@/components/chat/ChatInput';
import { ChatMessages } from '@/components/chat/ChatMessages';
import { telegramService } from '@/services/telegramService';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { generateAIResponse } from '@/services/aiService';
import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import type { Agent } from '@/types/agent';

interface Message {
  id: number;
  sender: 'user' | 'ai';
  text: string;
  timestamp: string;
}

const TelegramChat = () => {
  const [chatInput, setChatInput] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  // Load and establish company ID from various sources
  useEffect(() => {
    // First try to get from currentUser
    if (currentUser?.companyId) {
      console.log('TelegramChat: Setting company ID from currentUser:', currentUser.companyId);
      setCompanyId(currentUser.companyId);
      setIsLoadingCompany(false);
      return;
    }
    
    // Fallback to localStorage if currentUser is not available
    const savedCompanyId = localStorage.getItem('currentCompanyId');
    if (savedCompanyId) {
      console.log('TelegramChat: Setting company ID from localStorage:', savedCompanyId);
      setCompanyId(savedCompanyId);
      setIsLoadingCompany(false);
    } else {
      console.error('TelegramChat: No company ID available from any source');
      setCompanyId(undefined);
      setIsLoadingCompany(false);
    }
  }, [currentUser?.companyId]);

  // Fetch available agents
  const { data: agents = [] } = useQuery({
    queryKey: ['agents', companyId],
    queryFn: async () => {
      try {
        if (!companyId) {
          console.log('TelegramChat: Skipping agent query - no company ID');
          return [];
        }
        
        console.log('TelegramChat: Fetching agents for company:', companyId);
        let query = supabase
          .from('ai_agents')
          .select('*')
          .order('created_at', { ascending: false });
          
        // Filter by company if not super_admin
        if (currentUser?.type !== 'super_admin' && companyId) {
          query = query.eq('company_id', companyId);
        }
        
        const { data, error } = await query;

        if (error) throw error;

        const formattedAgents = data.map(agent => ({
          ...agent,
          avatar: agent.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${agent.name}`,
          icon: Bot,
          id: agent.id,
          name: agent.name,
          description: agent.description,
          model: agent.model,
          apiKey: agent.api_key || "",
          openaiApiKey: agent.openai_api_key || "",
          geminiApiKey: agent.gemini_api_key || "",
          prompt: agent.prompt,
          preferredApi: agent.preferred_api as "mistral" | "openai" | "gemini",
        }));

        return formattedAgents;
      } catch (error) {
        console.error('Erro ao carregar agentes:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os agentes.",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!companyId
  });

  // Connect to Telegram
  useEffect(() => {
    const initializeTelegram = async () => {
      try {
        await telegramService.initialize((messageData: any) => {
          console.log('Nova mensagem do Telegram:', messageData);
          
          const userMessage: Message = {
            id: Date.now(),
            sender: 'user',
            text: messageData.text || '',
            timestamp: new Date().toISOString()
          };
          
          setMessages(prev => [...prev, userMessage]);
          
          // Auto-reply with AI if an agent is selected
          if (selectedAgent?.apiKey) {
            handleAIResponse(messageData.text || '', messageData.chat_id);
          }
        });
      } catch (error) {
        console.error('Erro ao inicializar Telegram:', error);
        toast({
          title: "Erro",
          description: "Não foi possível conectar ao Telegram.",
          variant: "destructive",
        });
      }
    };
    
    initializeTelegram();
  }, [selectedAgent, toast]);

  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    
    try {
      const userMessage: Message = {
        id: Date.now(),
        sender: 'user',
        text: chatInput,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage]);
      setChatInput('');
      
      // Here we would send the message to a specific chat
      // For demo, we're just adding it to the UI
      if (selectedAgent?.apiKey) {
        const chatId = '123456789'; // This would be the actual chat ID in a real app
        await handleAIResponse(chatInput, chatId);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar a mensagem.",
        variant: "destructive",
      });
    }
  };

  const handleAIResponse = async (userMessage: string, chatId: string) => {
    try {
      const response = await generateAIResponse(userMessage, selectedAgent);
      
      if (response) {
        const aiMessage: Message = {
          id: Date.now(),
          sender: 'ai',
          text: response,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, aiMessage]);
        
        // In a real app, we would send this message to Telegram
        // await telegramService.sendMessage(chatId, response);
      }
    } catch (error) {
      console.error('Erro ao gerar resposta AI:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar uma resposta.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)]">
      <div className="border-b p-4">
        <h1 className="text-2xl font-bold">Telegram Chat</h1>
        <p className="text-muted-foreground">
          Interface de chat para bots do Telegram
        </p>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        <div className="w-1/4 border-r p-4">
          <h2 className="font-semibold mb-4">Selecionar Agente</h2>
          <div className="space-y-2">
            {agents.map((agent) => (
              <div 
                key={agent.id}
                className={`p-2 rounded cursor-pointer ${
                  selectedAgent?.id === agent.id ? 'bg-primary text-white' : 'hover:bg-secondary'
                }`}
                onClick={() => setSelectedAgent(agent)}
              >
                {agent.name}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-hidden">
            <ChatMessages messages={messages} />
          </div>
          
          <div className="p-4 border-t">
            <ChatInput
              value={chatInput}
              onChange={setChatInput}
              onSend={handleSendMessage}
              companyId={companyId || ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramChat;
